import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Lock, GraduationCap, Loader2 } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { authService } from "@/services/authService";
import { parseThrottleMessage } from "@/utils/helpers";

const AgencyLogin = () => {
    const { branding, loading } = useBranding();
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const primaryColor = branding?.branding?.colors?.primary || "#1d3658";

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoggingIn(true);
        setError(null);

        try {
            const user = await authService.agencyLogin(email, password);
            setUser(user);
            navigate("/partner");
        } catch (err: any) {
            const data = err.response?.data;
            const apiError = data?.error;

            if (apiError === "wrong_portal") {
                setError("This is a student account. Please use the Student Login page.");
                return;
            }

            if (apiError === "email_not_verified") {
                navigate("/partner/agency-register", {
                    state: {
                        verifyEmail: data?.email || email,
                        expiresIn: data?.expires_in,
                    },
                });
                return;
            }

            const errorMessage =
                err.response?.status === 429
                    ? parseThrottleMessage(data?.detail)
                    : data?.error ||
                      data?.detail ||
                      data?.non_field_errors?.[0] ||
                      "Invalid email or password. Please try again.";
            setError(errorMessage);
        } finally {
            setIsLoggingIn(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50/50 p-4 font-sans text-foreground">
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-secondary/10 rounded-full blur-3xl" />
            </div>

            <div className="w-full max-w-md relative z-10">
                <div className="flex flex-col items-center mb-8">
                    <Link to="/" className="flex flex-col items-center gap-3 mb-4 group text-center">
                        <div className="w-20 h-20 flex items-center justify-center">
                            {!loading && branding?.branding?.logo ? (
                                <img
                                    src={branding.branding.logo}
                                    alt={`${branding.name} Logo`}
                                    className="max-w-full max-h-full object-contain"
                                />
                            ) : (
                                <div
                                    className="w-full h-full rounded-lg flex items-center justify-center text-white"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    <GraduationCap className="w-12 h-12" />
                                </div>
                            )}
                        </div>
                        <span
                            className="font-serif text-3xl font-black text-primary text-center"
                            style={{ fontFamily: "var(--font-university-name)" }}
                        >
                            {!loading && branding?.name ? branding.name : "University Portal"}
                        </span>
                    </Link>
                    <h1 className="text-xl font-bold text-slate-900">Partner Login</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Sign in to your agency account
                    </p>
                </div>

                <Card className="border-none shadow-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
                    <CardContent className="p-8">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-700">
                                    Email Address
                                </Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="your.email@example.com"
                                        className="h-11 pl-10 border-slate-200 focus-visible:ring-1"
                                        style={{ borderColor: error ? "#ef4444" : undefined }}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-700">
                                    Password
                                </Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Enter your password"
                                        className="h-11 pl-10 border-slate-200 focus-visible:ring-1"
                                        style={{ borderColor: error ? "#ef4444" : undefined }}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="text-right">
                                    <Link
                                        to="/partner/forgot-password"
                                        className="text-xs hover:underline"
                                        style={{ color: primaryColor }}
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                            </div>

                            {error && (
                                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                    {error}
                                </div>
                            )}

                            <Button
                                type="submit"
                                className="w-full h-11 text-white hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: primaryColor }}
                                disabled={isLoggingIn}
                            >
                                {isLoggingIn ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Logging in...
                                    </>
                                ) : (
                                    "Log In"
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm text-slate-600">
                            Not a partner yet?{" "}
                            <Link
                                to="/partner/agency-register"
                                className="font-semibold hover:underline"
                                style={{ color: primaryColor }}
                            >
                                Become a Partner
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AgencyLogin;
